import Header from "@/components/ui/Header";
import { Colors } from "@/constants/Colors";
import Icons from "@/assets/icons";
import { Ionicons } from "@expo/vector-icons";
import React, { useMemo, useState } from "react";
import {
  SafeAreaView,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  Alert,
  StatusBar,
  Platform,
} from "react-native";
import useUserStore from "@/store/user";
import { router, useNavigation, useFocusEffect } from "expo-router";
import { storageUtils } from "@/utils/storage";
import { clearGlobalSessionId, getGlobalSessionId } from "@/services";
import { AddPaymentMethodCard, YourPlan } from "@/components/account";
import { ActionButton } from "@/components/ActionButton";
import ContactSupportBottomSheet from "@/components/ContactSupportBottomSheet";
import { formatDate } from "@/utils/date";
import CommonServices from "@/services/commonServices";
import { FontFamilies } from "@/constants/Typography";
import { APP_BASE_URL, IAP_ANNUAL_PLANS } from "@/constants/Configs";
import useWindow from "@/hooks/useWindow";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import { isWeb } from "@/utils/platform";
import FBTrackingService from "@/services/fbTrackingService";
import { AppText } from "@/components/AppText";

interface RowProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  value?: string;
  subValue?: string;
  showChevron?: boolean;
  onPress?: () => void;
  titleStyle?: StyleProp<TextStyle>;
}

const Row: React.FC<RowProps> = ({
  icon,
  title,
  subtitle,
  value,
  subValue,
  showChevron = true,
  onPress,
  titleStyle,
}) => {
  return (
    <TouchableOpacity style={styles.row} onPress={onPress}>
      <View style={styles.rowLeft}>
        {icon && <View style={styles.iconContainer}>{icon}</View>}
        <View style={styles.textContainer}>
          <Text style={[styles.rowTitle, titleStyle]}>{title}</Text>
          {subtitle && <Text style={styles.rowSubtitle}>{subtitle}</Text>}
        </View>
      </View>

      <View style={styles.rowRight}>
        <View style={styles.rowValueContainer}>
          {value && <Text style={styles.rowValue}>{value}</Text>}
          {subValue && <Text style={styles.rowSubValue}>{subValue}</Text>}
        </View>
        {showChevron && (
          <Ionicons name="chevron-forward" size={20} color="#9CA3AF" />
        )}
      </View>
    </TouchableOpacity>
  );
};

const AccountScreen = () => {
  const navigation = useNavigation();
  const { isDesktop } = useWindow();
  const [showContactSupport, setShowContactSupport] = useState(false);

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("account");
    }, [])
  );

  const { userProfile, getUserProfile, isFreePlan } = useUserStore();
  const plan = userProfile?.plan;
  // Require the period to still be in the future. After period_end Stripe
  // marks the sub `canceled` and `subscriptions.update()` is no longer allowed,
  // so showing Re-subscribe based on a stale plan would 400.
  const isCanceled =
    plan?.cancelAtPeriodEnd === true &&
    plan?.currentPeriodEnd != null &&
    new Date(plan.currentPeriodEnd) > new Date();
  const isPayAsYouGo = userProfile?.isPayAsYouGo;

  const handleSubscriptionEnded = () => {
    getUserProfile();
    const sessionId = getGlobalSessionId();
    if (Platform.OS === "web") {
      window.location.href = sessionId
        ? `${APP_BASE_URL}/pay?sessionId=${sessionId}&fromWebApp=true`
        : `${APP_BASE_URL}/pay`;
    } else {
      router.replace("/signup/onboard-paywall");
    }
  };

  const onResubscribePress = () => {
    CommonServices.reSubscribe().then((res) => {
      if (res.success) {
        getUserProfile();
        router.push("/account/resubscribe-success");
        return;
      }
      const errCode = Array.isArray(res.data) ? res.data[0]?.code : undefined;
      const message =
        res.message || "Your subscription has already ended.";
      if (errCode === "resubscribe:already_ended") {
        if (Platform.OS === "web") {
          alert(message);
          handleSubscriptionEnded();
        } else {
          Alert.alert("Subscription ended", message, [
            { text: "Subscribe again", onPress: handleSubscriptionEnded },
          ]);
        }
      }
    });
  };

  const planIAP = useMemo(() => {
    const productId = userProfile?.ipaSubscription?.productId;
    if (!productId) return null;
    return IAP_ANNUAL_PLANS;
  }, [userProfile]);

  const isIAP = !!planIAP;

  return (
    <SafeAreaView
      style={isDesktop ? styles.containerDesktop : styles.container}
    >
      <StatusBar
        barStyle="dark-content"
        translucent
        backgroundColor="transparent"
      />

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollViewContent}
      >
        <AppText style={styles.title}>Your Sparrow Account</AppText>
        {isFreePlan ? (
          <AddPaymentMethodCard />
        ) : (
          <View style={styles.planSection}>
            {!isPayAsYouGo && (
              <>
                {isCanceled && !isIAP ? (
                  <>
                    <Text
                      style={[styles.planSubtitle, styles.planSubtitleMargin]}
                    >
                      {`Ends on ${formatDate(
                        plan.currentPeriodEnd
                      )}. You will lose access to all class action claims in progress. `}
                    </Text>
                    <ActionButton
                      title="Re-subscribe"
                      onPress={onResubscribePress}
                    />
                  </>
                ) : null}
              </>
            )}
          </View>
        )}

        {/* Profile Settings */}
        <View style={styles.section}>
          <Row
            icon={<Icons.ProfileUser />}
            title="Personal details"
            onPress={() => router.push("/account/profile")}
          />
          <View style={styles.divider} />
          {!isIAP && (
            <Row
              icon={<Icons.ProfileBank />}
              title="Payment"
              subtitle="Manage payment methods & invoices"
              onPress={() => router.push("/account/payment")}
            />
          )}
          <View style={styles.divider} />
          <Row
            icon={<Icons.ProfileBank />}
            title="Linked banks and cards"
            onPress={() => router.push("/account/linked-banks")}
          />
          <View style={styles.divider} />
          <Row
            icon={<Icons.ProfileUser />}
            title="Linked email accounts"
            onPress={() => router.push("/account/linked-emails")}
          />
          <View style={styles.divider} />
          <Row
            icon={<Icons.EarnRewards />}
            title="Earn Rewards"
            subtitle="Complete offers to earn cash"
            onPress={() => router.push("/offers")}
          />
          <View style={styles.divider} />
          <Row
            icon={<Icons.ProfilePhone />}
            title="Contact support"
            onPress={() => setShowContactSupport(true)}
          />
        </View>
        <TouchableOpacity
          style={styles.logoutButton}
          onPress={async () => {
            const onLogout = async () => {
              storageUtils.removeSessionId();
              clearGlobalSessionId();
              setTimeout(() => {
                if (Platform.OS === "web") {
                  window.location.href = `${APP_BASE_URL}/logout`;
                } else {
                  navigation.reset({
                    index: 0,
                    routes: [{ name: "welcome" as never }],
                  });
                }
              }, 100);
            };
            if (Platform.OS === "web") {
              const result = confirm("Are you sure you want to logout?");
              if (result) {
                onLogout();
              }
              return;
            }
            Alert.alert("Logout", "Are you sure you want to logout?", [
              {
                text: "Cancel",
                style: "cancel",
              },
              {
                text: "Logout",
                onPress: () => onLogout(),
              },
            ]);
          }}
        >
          <Text style={styles.logoutButtonText}>Logout</Text>
        </TouchableOpacity>
        {/* Bottom spacing for tabbar */}
        <View style={styles.bottomSpacer} />
      </ScrollView>

      <ContactSupportBottomSheet
        isVisible={showContactSupport}
        onClose={() => setShowContactSupport(false)}
      />
    </SafeAreaView>
  );
};

export default AccountScreen;

const styles = StyleSheet.create({
  title: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: Colors.text,
    marginTop: 24,
    marginBottom: 8,
  },
  containerDesktop: {
    flex: 1,
    width: SCREEN_WIDTH,
    alignSelf: "center",
    backgroundColor: Colors.background,
  },
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 16,
  },
  scrollViewContent: {
    paddingBottom: 100,
  },
  section: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    marginTop: 16,
    overflow: "hidden",
  },
  planSection: {
    marginTop: 12,
    paddingBottom: 20,
  },
  planSubtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginBottom: 16,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 18,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
  },
  rowLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  iconContainer: {
    marginRight: 12,
  },
  textContainer: {
    flex: 1,
  },
  rowTitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
  },
  rowSubtitle: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 2,
  },
  rowSubValue: {
    fontSize: 14,
    color: "#6B7280",
    marginTop: 2,
  },
  rowRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rowValueContainer: {
    alignItems: "flex-end",
    gap: 4,
  },
  rowValue: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
  },
  logoutButton: {
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  annualPlanTitle: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 15,
  },
  planSubtitleMargin: {
    marginTop: 16,
  },
  logoutButtonText: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#848484",
  },
  divider: {
    height: 0.5,
    backgroundColor: "#E7E7E7",
  },
  bottomSpacer: {
    height: 100,
  },
});
